import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Loader, Center, Stack, Text } from '@mantine/core';
import { useIsMineClient } from '../../../application/store/authStore';
import { useAuth } from '../../hooks/useAuth';
import { PUBLIC_ROUTES, UTILITY_ROUTES } from '../../routing/routes';

/**
 * MineClientRoute — route guard for the client area (sprint-217, epic-036 Phase 1 / 1D).
 *
 * Requires authentication AND one of the three mine-scoped client roles. Mirrors
 * `MineMinerRoute`'s loading/redirect contract so the two areas behave identically at the
 * edges; what differs is who gets in.
 *
 * 🔴 THIS GUARD DECIDES WHICH SCREEN TO DRAW, NOT WHAT DATA MAY BE READ.
 *
 * It reads role names off the persisted login result. That is enough to answer "does this
 * person belong in the client area", and it is nowhere near enough to answer "may they see
 * mine 1234567" — which is resolved server-side against a live MineMembership row on every
 * request (DR-005 clause 2 / sprint-215 D3). Passing this guard grants nothing.
 *
 * ⚠️ A client user with a role but no memberships passes deliberately. They are a real,
 * expected state — invited but not yet attached to a mine — and bouncing them to
 * /access-denied would say "you are not allowed here" when the truth is "there is nothing
 * here yet". The landing page says the true thing instead.
 */
export const MineClientRoute: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isLoading } = useAuth();
  const isMineClient = useIsMineClient();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={PUBLIC_ROUTES.LOGIN} replace />;
  }

  if (!isMineClient) {
    return <Navigate to={UTILITY_ROUTES.ACCESS_DENIED} replace />;
  }

  return <Outlet />;
};
